import React, { useEffect, useRef } from "react";
import Highcharts, { Options } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  AvgBarleyWheatChart,
  ChartBarleySoftWheat,
} from "../../../redux/actions/dashboard/dashboardTypes";
import { useSelector } from "react-redux";
import { DashboardSelectors } from "../../../redux/selectors/unifiedSelectors";

interface DynamicChartCountActionsProps {
  dynamicChart: ChartBarleySoftWheat[];
  avgProtWheat: AvgBarleyWheatChart[];
  t: (arg0: string) => string;
  loading: boolean;
  selectedCrop: { slug: string; translation: string };
}

const DynamicChartCountActions: React.FC<DynamicChartCountActionsProps> = ({
  dynamicChart,
  avgProtWheat,
  t,
  loading,
  selectedCrop,
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { activityData } = useSelector(DashboardSelectors);

  const findTheColor = activityData.data.find(
    (item) =>
      item.specie.toLocaleLowerCase() ===
      selectedCrop.translation.toLocaleLowerCase(),
  )?.color;

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart) {
      if (loading) {
        chart.showLoading(`...${t("Common_options.Loading")}`);
      } else {
        chart.hideLoading();
      }
    }
  }, [loading, t]);

  const categories = dynamicChart.map((u: ChartBarleySoftWheat) => u.variety);

  const chartOptions: Options = {
    chart: {
      type: "column",
      style: {
        fontFamily: "inherit",
      },
      backgroundColor: "#ffffff",
    },
    loading: {
      hideDuration: 300,
      showDuration: 300,
      labelStyle: {
        fontSize: "16px",
        fontWeight: "bold",
        color: "#000000",
      },
      style: {
        backgroundColor: "rgba(255, 255, 255, 0.8)",
      },
    },
    title: {
      text: t("Dashboard.cereal_charts.distribution_by_variety"),
      align: "left",
      style: {
        fontSize: "16px",
        fontWeight: "bold",
        color: "#1a1a1a",
      },
    },
    xAxis: {
      categories,
      crosshair: true,
      lineColor: "#e0e0e0",
      tickColor: "#e0e0e0",
      labels: {
        style: { color: "#555555" },
      },
    },
    yAxis: [
      {
        // Left axis — protein rate (%)
        title: {
          text: t("Dashboard.cereal_charts.protein_levels_variety"),
          style: { color: "#555555", fontSize: "11px" },
          rotation: -90,
        },
        labels: {
          format: "{value}%",
          style: { color: "#555555" },
        },
        gridLineColor: "#eeeeee",
        min: 0,
      },
      {
        // Right axis — number of actions
        title: {
          text: t("Dashboard.number_of_actions"),
          style: { color: "#555555", fontSize: "11px" },
          rotation: 90,
        },
        labels: {
          style: { color: "#555555" },
        },
        opposite: true,
        gridLineWidth: 0,
        min: 0,
      },
    ],
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      symbolRadius: 50,
      itemStyle: {
        color: "#555555",
        fontWeight: "normal",
      },
    },
    tooltip: {
      shared: true,
    },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        pointPadding: 0.05,
        groupPadding: 0.15,
      },
    },
    series: [
      {
        name: t("Dashboard.protein_rate"),
        type: "column",
        yAxis: 0,
        data: avgProtWheat.map((u: AvgBarleyWheatChart) => u.average),
        color: "#e5ae72",
      },
      {
        name: t("Dashboard.number_of_actions"),
        type: "column",
        yAxis: 1,
        data: dynamicChart.map((u: ChartBarleySoftWheat) => u.actions),
        color: findTheColor,
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return (
    <HighchartsReact
      ref={chartRef}
      containerProps={{
        style: {
          width: "100%",
        },
      }}
      highcharts={Highcharts}
      options={chartOptions}
    />
  );
};

export default DynamicChartCountActions;
